import { onMounted, onUnmounted, ref, computed } from 'vue'

/**
 * Reactive breakpoint state.
 *
 * Uses `matchMedia` rather than a resize listener: the browser only notifies
 * on an actual breakpoint crossing, instead of firing on every pixel of a
 * drag. Values match the SCSS breakpoints in `_tokens.scss`.
 *
 * This decides *structure* — whether a table renders as rows or cards, whether
 * the sidebar exists. Anything purely visual should be a CSS media query
 * instead, so it works before JS has run.
 */
const QUERIES = {
  xs: '(min-width: 390px)',
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
}

export function useBreakpoint() {
  const matches = ref({ xs: false, sm: false, md: false, lg: false, xl: false })
  const listeners = []

  onMounted(() => {
    for (const [name, query] of Object.entries(QUERIES)) {
      const media = window.matchMedia(query)
      matches.value[name] = media.matches

      const handler = (event) => {
        matches.value = { ...matches.value, [name]: event.matches }
      }
      media.addEventListener('change', handler)
      listeners.push(() => media.removeEventListener('change', handler))
    }
  })

  onUnmounted(() => listeners.forEach((remove) => remove()))

  return {
    matches,
    isMobile: computed(() => !matches.value.md),
    isTablet: computed(() => matches.value.md && !matches.value.lg),
    // `lg` is where the sidebar appears and tables become tables.
    isDesktop: computed(() => matches.value.lg),
    isWide: computed(() => matches.value.xl),
  }
}

/**
 * True while the user has asked for reduced motion.
 *
 * Read synchronously at setup rather than in `onMounted`. That distinction
 * matters more than it looks: components build their animation props during
 * the first render, and a value that is still `false` at that point bakes the
 * animated variant into a plain object that never re-evaluates. The visible
 * symptom is content parked at zero opacity for exactly the people who asked
 * not to be animated at.
 */
export function usePrefersReducedMotion() {
  const query = '(prefers-reduced-motion: reduce)'
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function'

  const prefers = ref(supported ? window.matchMedia(query).matches : false)
  const handler = (event) => (prefers.value = event.matches)

  let media = null
  onMounted(() => {
    if (!supported) return
    media = window.matchMedia(query)
    // Re-read in case it changed between setup and mount.
    prefers.value = media.matches
    media.addEventListener('change', handler)
  })

  onUnmounted(() => media?.removeEventListener('change', handler))
  return prefers
}
